// /Users/NANDA/Documents/Development/NIN/nin-terminal/app/api/(plugins)/ai-hedgefund/aiHedgeFundTypes.ts
// Centralized types and schemas for the AI Hedge Fund plugin

import { z } from 'zod';

// --- Financial Data --- 
// Define our simplified FinancialData structure for the frontend
export const FinancialDataSchema = z.object({
  symbol: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  sector: z.string().optional(),
  industry: z.string().optional(),
  marketCap: z.number().nullable().optional(),
  peRatio: z.number().nullable().optional(),
  pegRatio: z.number().nullable().optional(),
  eps: z.number().nullable().optional(),
  dividendYield: z.number().nullable().optional(),
  beta: z.number().nullable().optional(),
  high52Week: z.number().nullable().optional(),
  low52Week: z.number().nullable().optional(),
  ma50Day: z.number().nullable().optional(),
  ma200Day: z.number().nullable().optional(),
  // Add PriceToBook and ReturnOnEquity for Buffett rules
  priceToBookRatio: z.number().nullable().optional(),
  returnOnEquity: z.number().nullable().optional(),
});

export type FinancialData = z.infer<typeof FinancialDataSchema>;

// --- Sentiment Data --- 
// Define our simplified SentimentData structure
export const SentimentDataSchema = z.object({
  newsSentimentScore: z.number().nullable().optional(), // e.g., -1 to 1
  socialMediaTrend: z.enum(['Bullish', 'Bearish', 'Neutral']).nullable().optional(),
  // Potentially add fields from Alpha Vantage News/Sentiment if implemented later
  // Example: overall_sentiment_score, overall_sentiment_label from AV feed
});

export type SentimentData = z.infer<typeof SentimentDataSchema>;

// --- Technical Data --- 
// Our Simplified TechnicalData Schema
export const TechnicalDataSchema = z.object({
  rsi: z.number().nullable().optional(),
  macd: z.object({
    macd: z.number().nullable().optional(),
    signal: z.number().nullable().optional(),
    histogram: z.number().nullable().optional(),
  }).nullable().optional(),
  // Add other indicators as needed
});

export type TechnicalData = z.infer<typeof TechnicalDataSchema>;

// --- Insight Data ---
// Define the structure for the insight response
export const InsightDataSchema = z.object({
  ticker: z.string(),
  decision: z.enum(['Buy', 'Sell', 'Hold']),
  reason: z.string(),
  confidence: z.number().min(0).max(1).optional(), // Optional confidence score 0-1
  supportingData: z.object({ // Include the data used for the decision
    financials: FinancialDataSchema.optional(),
    technicals: TechnicalDataSchema.optional(),
    sentiment: SentimentDataSchema.optional(),
  }).optional(),
});

export type InsightData = z.infer<typeof InsightDataSchema>;

// --- Agent Types ---
// Define possible agent personas
export const AgentEnum = z.enum(['Default', 'Buffett']); // Add more agents here later
export type AgentType = z.infer<typeof AgentEnum>;